import {
  createConnection,
  ERR_INVALID_AUTH,
  type Auth,
  type Connection,
} from 'home-assistant-js-websocket'
import type { ConnState, EntityDiff, HaGateway, HistoryPoint, ServiceCall, Unsub } from '../types'
import { EntityDecoder, type EntitiesMessage } from './decode'
import { clearTokens, HASS_URL, redirectToLogin, resolveAuth } from './auth'
import { Keepalive } from './keepalive'

type DiffCb = (diff: EntityDiff) => void

export class RealGateway implements HaGateway {
  readonly mode = 'real' as const

  private auth: Auth | null = null
  private conn: Connection | null = null
  private decoder = new EntityDecoder()
  private keepalive: Keepalive | null = null
  private wsUnsub: Unsub | null = null

  private subscribedIds: string[] = []
  private diffCb: DiffCb | null = null

  private state: ConnState = { phase: 'idle', lastPongAt: null, lastError: null }
  private readonly stateListeners = new Set<(s: ConnState) => void>()

  async start(): Promise<void> {
    this.setState({ phase: 'connecting' })
    try {
      this.auth = await resolveAuth()
    } catch {
      // no saved tokens → bounce to HA login (navigates away)
      this.setState({ phase: 'auth-required' })
      await redirectToLogin()
      return
    }

    try {
      this.conn = await createConnection({ auth: this.auth })
    } catch (err) {
      if (err === ERR_INVALID_AUTH) {
        clearTokens()
        this.setState({ phase: 'auth-error', lastError: 'Ugyldig pålogging' })
        await redirectToLogin()
        return
      }
      this.setState({ phase: 'reconnecting', lastError: String(err) })
      throw err
    }

    this.wireConnection(this.conn)
    this.setState({ phase: 'connected', lastError: null })

    this.keepalive = new Keepalive(
      () => this.ping(),
      () => void this.conn?.reconnect(true),
      (at) => this.setState({ lastPongAt: at }),
    )
    this.keepalive.start()

    // if a subscription was requested before we connected, (re)establish it
    if (this.diffCb && this.subscribedIds.length) this.openEntitySub()
  }

  private wireConnection(conn: Connection): void {
    conn.addEventListener('ready', () => {
      // HA restarted or socket re-established: resync entity snapshot
      this.decoder.reset()
      this.setState({ phase: 'connected', lastError: null })
      this.keepalive?.markPong()
      if (this.diffCb && this.subscribedIds.length) this.openEntitySub()
    })
    conn.addEventListener('disconnected', () => this.setState({ phase: 'reconnecting' }))
    conn.addEventListener('reconnect-error', (_c, err) => {
      if (err === ERR_INVALID_AUTH) {
        clearTokens()
        this.setState({ phase: 'auth-error', lastError: 'Pålogging utløpt' })
        void redirectToLogin()
      } else {
        this.setState({ phase: 'reconnecting', lastError: String(err) })
      }
    })
  }

  subscribeEntities(ids: string[], cb: DiffCb): Unsub {
    this.subscribedIds = ids
    this.diffCb = cb
    if (this.conn) this.openEntitySub()
    return () => {
      this.diffCb = null
      this.wsUnsub?.()
      this.wsUnsub = null
    }
  }

  private openEntitySub(): void {
    if (!this.conn || !this.diffCb) return
    this.wsUnsub?.()
    const cb = this.diffCb
    // subscribe_entities with a fixed allow-list — HA replays this on reconnect
    void this.conn
      .subscribeMessage<EntitiesMessage>(
        (msg) => {
          this.keepalive?.markPong()
          cb(this.decoder.apply(msg))
        },
        { type: 'subscribe_entities', entity_ids: this.subscribedIds },
      )
      .then((unsub) => {
        this.wsUnsub = unsub
      })
      .catch((err) => this.setState({ phase: 'reconnecting', lastError: String(err) }))
  }

  async callService(call: ServiceCall): Promise<void> {
    if (!this.conn) throw new Error('not connected')
    await this.conn.sendMessagePromise({
      type: 'call_service',
      domain: call.domain,
      service: call.service,
      service_data: call.data,
      target: call.target,
    })
  }

  async fetchHistory(entityId: string, hours: number): Promise<HistoryPoint[]> {
    if (!this.conn) return []
    const end = new Date()
    const start = new Date(end.getTime() - hours * 3600_000)
    const res = await this.conn.sendMessagePromise<Record<string, Array<{ s: string; lu: number }>>>({
      type: 'history/history_during_period',
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      entity_ids: [entityId],
      minimal_response: true,
      no_attributes: true,
    })
    const rows = res[entityId] ?? []
    const points: HistoryPoint[] = []
    for (const r of rows) {
      const v = Number(r.s)
      if (Number.isFinite(v)) points.push({ t: Math.round(r.lu * 1000), v })
    }
    return points
  }

  cameraSnapshotUrl(entityId: string): string | null {
    // the entity_picture already carries a signed access token
    const pic = this.lastPicture.get(entityId)
    return pic ? `${HASS_URL}${pic}` : null
  }
  private readonly lastPicture = new Map<string, string>()
  /** fed by the store so cameraSnapshotUrl stays current without a second subscription */
  noteEntityPicture(entityId: string, picture: string | undefined): void {
    if (picture) this.lastPicture.set(entityId, picture)
  }

  async ping(): Promise<void> {
    if (!this.conn) throw new Error('not connected')
    await this.conn.ping()
  }

  getConnState(): ConnState {
    return this.state
  }
  onConnState(cb: (s: ConnState) => void): Unsub {
    this.stateListeners.add(cb)
    cb(this.state)
    return () => this.stateListeners.delete(cb)
  }
  private setState(patch: Partial<ConnState>): void {
    this.state = { ...this.state, ...patch }
    for (const l of this.stateListeners) l(this.state)
  }

  destroy(): void {
    this.keepalive?.dispose()
    this.wsUnsub?.()
    this.conn?.close()
    this.conn = null
    this.stateListeners.clear()
  }
}
